import styled from "@emotion/styled/macro";
import { Fragment } from "react";
import {
  SvgIcon,
  SvgNames,
  Tooltip,
  Typography,
  tokens,
} from "@sunrun/experience-ui-components";
import { CardCarouselData } from "providers/Types";
import { formatCardValueWithSuffix } from "utils/formatCardValue";

type SystemDataOverlayProps = {
  data: CardCarouselData[];
  variant?: "overlay" | "inline";
};

const SystemDataOverlay = ({
  data,
  variant = "overlay",
}: SystemDataOverlayProps) => (
  <Wrapper $variant={variant}>
    <SystemData>
      {data.map((card, index) => (
        <Fragment key={`${card.headerText}-${index}`}>
          <SystemDataItem>
            <SystemDataText size={20} fontWeight="medium" color={tokens.WHITE}>
              {formatCardValueWithSuffix(card)}
            </SystemDataText>
            <SystemDataHeaderRow>
              <SystemDataText size={12} color={tokens.WHITE}>
                {card.headerText.replace("Estimated", "Est.")}
              </SystemDataText>
              {card.infoToolTipText && (
                <StyledTooltip content={card.infoToolTipText} direction="top">
                  <SvgIcon
                    name={SvgNames.InfoCircle}
                    width={14}
                    height={14}
                    color={tokens.WHITE}
                  />
                </StyledTooltip>
              )}
            </SystemDataHeaderRow>
          </SystemDataItem>
          {index < data.length - 1 && <Divider />}
        </Fragment>
      ))}
    </SystemData>
  </Wrapper>
);

const Wrapper = styled.div<{ $variant: "overlay" | "inline" }>`
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${({ $variant }) =>
    $variant === "overlay"
      ? `
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px;
    z-index: 1;
    background: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.85) 0%,
      rgba(0, 0, 0, 0) 85%
    );
  `
      : ""}
`;

const SystemData = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;

  @media (max-width: 649px) {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 16px;

    > *:nth-child(4n) {
      display: none;
    }
  }
`;

const SystemDataItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SystemDataHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledTooltip = styled(Tooltip)`
  .sr-tooltip-content,
  .sr-tooltip-content-mobile {
    min-width: 240px;
    max-width: 320px;
    padding: 16px;
    font-size: 12px;
    line-height: 1.4;

    @media (min-width: 1024px) {
      padding: 24px;
      font-size: 14px;
      line-height: 20px;
    }
  }
`;

const Divider = styled.div`
  width: 1px;
  align-self: stretch;
  background-color: ${tokens.WHITE};
  flex-shrink: 0;
`;

const SystemDataText = styled(Typography)`
  text-align: center !important;
`;

export { SystemDataOverlay };
